// The simulation step. One function advances the whole world by N sim-hours,
// used identically by the live 1Hz loop and by offline catch-up — so a farm
// left alone for a day lands in the same place either way.

import { clamp, round2 } from '../core/math';
import type { Rng } from '../core/rng';
import { rollHourlyAccess } from './access';
import type {
  ChickenEntity,
  ConditionId,
  GameState,
  StoredEgg,
} from '../core/types';
import { recalculateHierarchy, shadeEscape, stepBehavior } from './behavior';
import { rollStream } from './league';
import { getConfig, type BiologyConfig, type Profile } from './config';
import {
  addCondition,
  applyEnvironmentalConditions,
  rollInfections,
  stepConditions,
} from './disease';
import { rollEscape } from './escape';
import { stepVeteranRun, stepMortality } from './veteran';
import { stepEventEggs } from './eventBirds';
import { stepIncubation } from './incubation';
import { stepPredatorNight, type PredatorOutcome } from './predatorNight';
import {
  advanceOvulation,
  beginLaying,
  createEgg,
  dailyLayProbability,
  isDueToLay,
  shouldLearnEggEating,
} from './eggs';
import { isSilkieFeathered } from './genetics';
import { brooderHealthLossPerHour, shouldStartMolt, stageForAge } from './lifecycle';
import { stepNeighbors } from './neighbors';
import { evaluateUnlocks } from './progression';
import { clampPhysiology, stepPhysiology } from './physiology';
import { effectsOf } from './items';
import { ensureSpots, isStructureRelearning } from './spots';
import { stepMicroclimate } from './microclimate';
import { stepLeagueStorm } from './storm';
import { stepLeagueWeather } from './forecast';
import { feedStageReminders, stepFeed, type FeedStageReminder } from './feed';
import { stepGear, type GearStepReport } from './gear';
import { dayOf } from './time';
import {
  buildBiosecurityContext,
  buildEnvContext,
  stepFacilities,
  summarizeFacilities,
} from './welfare';

/** Everything notable that happened during a step, for toasts and summaries. */
export interface StepReport {
  eggsLaid: Array<{ chickenId: string; egg: StoredEgg }>;
  newConditions: Array<{ chickenId: string; condition: ConditionId }>;
  recoveries: Array<{ chickenId: string; condition: ConditionId }>;
  escapes: string[];
  neighborEvent: 'none' | 'knock' | 'warning' | 'fine';
  fine: number;
  moltsStarted: string[];
  stageChanges: Array<{ chickenId: string; stage: ChickenEntity['stage'] }>;
  deaths: string[];
  /** Event eggs that hatched this step (EVENT-CODES.md §1). */
  hatched: string[];
  /** A predator came by — breached or merely tried. Veteran-only. */
  predator: PredatorOutcome | null;
  /**
   * Birds whose ration stopped matching them when they changed stage
   * (PLAN-04 S5). Normal only; S12 turns these into the reminder chip.
   */
  feedReminders: FeedStageReminder[];
  /** Chicks that fell into an open drinker, and any that drowned (PLAN-04 S6). */
  gear: GearStepReport;
}

function emptyReport(): StepReport {
  return {
    eggsLaid: [],
    newConditions: [],
    recoveries: [],
    escapes: [],
    neighborEvent: 'none',
    fine: 0,
    moltsStarted: [],
    stageChanges: [],
    deaths: [],
    hatched: [],
    predator: null,
    feedReminders: [],
    gear: { soaked: [], drowned: [] },
  };
}

export interface StepOptions {
  /** Sim-hours to advance. Live ticks pass a tiny slice; catch-up passes 0.25. */
  hours: number;
  /** Offline replay applies grace floors and skips predators. */
  offline?: boolean;
  profile?: Profile;
}

export function stepSimulation(state: GameState, options: StepOptions): StepReport {
  const { hours } = options;
  const profile: Profile = options.profile ?? (state.veteran.enabled ? 'veteran' : 'normal');
  const config = getConfig(profile);
  const report = emptyReport();
  if (hours <= 0) return report;

  // Keyed to the sim clock, which advances on every step in both the live and
  // the offline path. Keying to the whole hour instead would hand every tick in
  // that hour the same sequence — birds would move in lockstep and every
  // disease roll in the hour would be the same roll repeated.
  // 6dp resolves a single real-time tick (1/3600 h ≈ 0.000278).
  // Veteran draws from the league seed instead of the save's (PLAN-04 S1), so
  // every player in the season faces the same rolls on the same day.
  const rng = rollStream(state, `sim:${state.simHour.toFixed(6)}`);

  // A league day has one weather for the whole season (REFOCUS §11), so the air
  // every bird below is charged for is the season's and not this save's city.
  // It is a pure function of the league day, so a replay of the same log against
  // the same season reaches the same air; Normal is left entirely alone.
  stepLeagueWeather(state);
  // The storm the forecast promised. Same reason and same place as the air: it
  // is a fact about the league day rather than about this save, and a replay
  // driving `stepSimulation` has to meet it here or the phone and the server
  // disagree about whether a hurricane happened.
  stepLeagueStorm(state);

  // Equipment bought or loaded since the last step takes its spot first.
  ensureSpots(state);
  // Who reaches water and who reaches the feed, rolled once per sim-hour and
  // reused by every step inside it (PLAN-04 S4). This runs before the per-bird
  // loop so a bird is charged for the access she had at the top of the step.
  rollHourlyAccess(state, config);
  const facilities = summarizeFacilities(state);
  const biosecurity = buildBiosecurityContext(state, facilities);
  // Offline grace keeps stats off the floor so nobody returns to a disaster.
  const floor = options.offline ? config.grace.minStatWhileAway : config.grace.criticalFloor;

  for (const chicken of state.flock) {
    if (!chicken.alive) continue;
    // Decided before physiology runs, so the temperature this bird is charged
    // for the step and the tile she walks to are the same tile. A catch-up step
    // covers hours at a time, and billing her for those hours in the sun she
    // left in the first minute of them is how a yard with a mango tree in it
    // was killing birds of heat stroke.
    const escape = shadeEscape(chicken, state, config, hours);
    const env = buildEnvContext(
      chicken,
      state,
      facilities,
      config,
      escape?.reachable ? escape.spot.id : undefined,
    );

    // --- Age and life stage ---
    // Never round an accumulator. One live tick is 1/3600 h in Real-Time and
    // 1/37.5 h in Simulation, so a day's worth of age is 1.16e-5 or 1.1e-3 —
    // both of which round2 flattens to zero. Rounding here froze every bird at
    // fourteen days forever: no pullets, no point of lay, no eggs, no lifecycle
    // at all. The 2dp rule (AGENT.md §3) is for the 0-100 stats it was written
    // for, not for a quantity that only ever grows by slivers.
    chicken.ageInDays += hours / 24;
    const stage = stageForAge(chicken.ageInDays);
    if (stage !== chicken.stage) {
      chicken.stage = stage;
      report.stageChanges.push({ chickenId: chicken.id, stage });
    }

    // --- Physiology ---
    stepPhysiology(chicken, env, {
      hours,
      config,
      isChick: chicken.stage === 'chick',
      floorAt: floor,
    });

    // Brooder error costs condition directly — chicks cannot thermoregulate.
    if (env.brooderTsi !== undefined) {
      const loss = brooderHealthLossPerHour(env.brooderTsi, config) * hours;
      if (loss > 0) {
        chicken.physiology.bodyCondition -= loss;
        clampPhysiology(chicken.physiology, floor);
      }
    }

    // --- Conditions ---
    for (const id of applyEnvironmentalConditions(
      chicken,
      env,
      state.simHour,
      config,
      isSilkieFeathered(chicken.genotype),
    )) {
      report.newConditions.push({ chickenId: chicken.id, condition: id });
    }

    const infections = options.offline
      ? [] // Illness can start offline but nothing new is rolled while away.
      : rollInfectionsFor(chicken, env, biosecurity, hours, config, rng);
    for (const id of infections) {
      addCondition(chicken, id, state.simHour);
      report.newConditions.push({ chickenId: chicken.id, condition: id });
    }

    for (const id of stepConditions(chicken, env, state.simHour, config)) {
      report.recoveries.push({ chickenId: chicken.id, condition: id });
    }

    // --- Molt ---
    if (shouldStartMolt(chicken, config)) {
      // A real hard molt runs 8-16 weeks and no two hens run it the same. The
      // flat 49 days was both below the real low end and identical for every
      // bird, so a whole flock stopped laying and resumed on the same day.
      const { durationMinDays: min, durationMaxDays: max, durationDays } = config.molt;
      const days = min !== undefined && max !== undefined ? rng.int(min, max) : durationDays;
      chicken.moltingUntilDay = dayOf(state.simHour) + days;
      report.moltsStarted.push(chicken.id);
    }

    // --- Behaviour ---
    stepBehavior(chicken, state, { facilities, config, rng, hours, escape });

    // --- Laying ---
    const egg = maybeLayEgg(chicken, state, facilities, config, rng);
    if (egg) report.eggsLaid.push({ chickenId: chicken.id, egg });

    // --- Veteran: death on the real veterinary timescales ---
    // Not a stat hitting zero — hours of a specific exposure, per
    // clay-farm-science 07-physiology-limits. Normal mode never gets here.
    if (!config.grace.sickNeverDie) {
      const mortality = stepMortality(chicken, env, state.weather.humidity, hours, config);
      if (mortality.died) {
        chicken.alive = false;
        chicken.deathCause = mortality.cause;
        chicken.diedOnDay = dayOf(state.simHour);
        report.deaths.push(chicken.id);
      }
    }
  }

  // --- Escape checks, on their own interval ---
  if (!options.offline && state.tickCount % config.escape.checkIntervalTicks === 0) {
    const perimeter = {
      fenceHeightInches: facilities.fenceHeightInches,
      gateUnlatched: facilities.anyDoorOpen,
      cooped: false,
    };
    for (const chicken of state.flock) {
      if (chicken.currentState === 'ROOSTING' || !chicken.alive) continue;
      if (rollEscape(chicken, perimeter, config, rng)) {
        chicken.currentState = 'ESCAPED';
        report.escapes.push(chicken.id);
      }
    }
  }

  // --- World systems ---
  stepFacilities(state, hours, config);
  // Feed ages, rats eat, mould gets in and the calcium clocks run (PLAN-04 S5).
  // After the per-bird loop so a bird is charged for the feed she had at the
  // start of the step, which is what the drain in `stepFacilities` just did.
  for (const started of stepFeed(state, hours, config, rng)) {
    report.newConditions.push(started);
  }
  if (!config.grace.sickNeverDie) {
    // Normal shows the keeper what a stage change just cost her; Veteran does
    // not explain itself, which is the whole contract of the mode.
    for (const reminder of feedStageReminders(
      state,
      report.stageChanges.map((c) => c.chickenId),
    )) {
      report.feedReminders.push(reminder);
    }
  }
  // Gear that is built for another age, and the deep waterer (PLAN-04 S6).
  report.gear = stepGear(state, hours, config, config.grace.sickNeverDie);
  for (const id of report.gear.drowned) report.deaths.push(id);
  // Each drinker settles toward the air at its own spot, so a hopper in the sun
  // and a fount in the mango shade end the afternoon at different temperatures
  // (PLAN-04 S3).
  stepMicroclimate(state, hours, config);
  const neighbor = stepNeighbors(state, hours, config);
  report.neighborEvent = neighbor.event;
  report.fine = neighbor.fine;
  // A friendly knock is a heads-up, not a complaint. Anything past that breaks
  // the quiet-day streak that `good_neighbor` is counting.
  if (neighbor.event === 'warning' || neighbor.event === 'fine') {
    state.progression.complainedToday = true;
  }
  evaluateUnlocks(state);
  if (neighbor.fine > 0) state.eggits = Math.max(0, state.eggits - neighbor.fine);

  // Event eggs hatch on their own clock, before the hierarchy is recomputed so
  // a new chick is ranked with everyone else.
  for (const chick of stepEventEggs(state, rng)) {
    report.hatched.push(chick.id);
  }
  // R5: bred eggs under the lamp run the full 21-day incubation.
  for (const chick of stepIncubation(state, rng)) {
    report.hatched.push(chick.id);
  }

  // Predators resolve once a night rather than per tick, and not while away —
  // nobody should return from a day offline to find the flock was raided. The
  // roll is on its own stream, not this step's, so it is not re-drawn by how
  // many times the per-bird loop above happened to roll.
  if (!options.offline) {
    report.predator = stepPredatorNight(state, config);
    for (const id of report.predator?.deaths ?? []) report.deaths.push(id);
  }

  recalculateHierarchy(state);
  // The veteran run clock only advances while something is still alive.
  stepVeteranRun(state, hours);
  // Full precision deliberately: a real-time tick is 1/3600 of an hour, and
  // rounding the clock to 2dp would swallow it whole and freeze time. The 2dp
  // rule in AGENT.md §3 is about chicken stats, not the world clock.
  state.simHour += hours;
  return report;
}

function rollInfectionsFor(
  chicken: ChickenEntity,
  env: ReturnType<typeof buildEnvContext>,
  biosecurity: ReturnType<typeof buildBiosecurityContext>,
  hours: number,
  config: BiologyConfig,
  rng: Rng,
): ConditionId[] {
  // Quarantined birds are isolated from the flock's disease pressure.
  if (chicken.quarantinedUntilHour !== undefined) return [];
  return rollInfections(chicken, env, biosecurity, hours, config, rng);
}

/** Lays an egg if the ovulation clock is due and the odds land. */
function maybeLayEgg(
  chicken: ChickenEntity,
  state: GameState,
  facilities: ReturnType<typeof summarizeFacilities>,
  config: BiologyConfig,
  rng: Rng,
): StoredEgg | null {
  if (chicken.gender !== 'hen') return null;
  // Has to run before the due check, not inside it — see beginLaying.
  beginLaying(chicken, state.simHour);
  if (!isDueToLay(chicken, state.simHour)) return null;

  const probability = dailyLayProbability(
    chicken,
    {
      weather: state.weather,
      hasCalcium: facilities.hasCalcium,
      hasCoopLight: facilities.hasCoopLight,
      hasNestBox: facilities.nestBoxes.length > 0,
      currentDay: dayOf(state.simHour),
    },
    config,
  );
  advanceOvulation(chicken, state.simHour, config);
  if (probability <= 0 || !rng.chance(probability)) return null;

  const egg = createEgg(chicken, state.simHour, rng);
  chicken.eggs.totalLaid++;

  // Without a nest box she lays on the ground; the egg still exists, but the
  // player has to notice it. With one, it goes where it belongs — unless the
  // box was just moved and the hens have not accepted its new spot yet
  // (REFOCUS §7), in which case she lays on the floor too.
  const nest = facilities.nestBoxes.find(
    (n) =>
      (n.state.eggs?.length ?? 0) < (effectsOf(n).capacity ?? 6) &&
      !isStructureRelearning(state, n),
  );
  if (nest) {
    nest.state.eggs = [...(nest.state.eggs ?? []), egg];
    // Eggs left sitting are how a hen learns to eat them.
    if (shouldLearnEggEating(nest.state.eggs, state.simHour, config)) {
      chicken.eggs.eggEatingHabit = true;
    }
  }
  return egg;
}

/** Clamped flock-wide summary used by the HUD and the dev dashboard. */
export function flockAverages(state: GameState): { hydration: number; energy: number } {
  const alive = state.flock.filter((c) => c.alive);
  if (alive.length === 0) return { hydration: 0, energy: 0 };
  const sum = alive.reduce(
    (acc, c) => ({
      hydration: acc.hydration + c.physiology.hydration,
      energy: acc.energy + c.physiology.energyReserve,
    }),
    { hydration: 0, energy: 0 },
  );
  return {
    hydration: round2(clamp(sum.hydration / alive.length, 0, 100)),
    energy: round2(clamp(sum.energy / alive.length, 0, 100)),
  };
}
